import { Fragment, useEffect, useState } from 'react';
import { Col, Row, Breadcrumb, Card, Tab, Nav, Form, Offcanvas, Button, Spinner } from 'react-bootstrap';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Import the new components
import OffcanvasCreateEmailTemplate from './OffcanvasCreateEmailTemplate';
import ModalDeleteEmailTemplate from './ModalDeleteEmailTemplate';

const EmailTemplateSettings = ({ activeKey }) => { // activeKey als Prop erhalten
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(true); // State for loading table
	const [isLoadingButton, setIsLoadingButton] = useState(false); // State for loading spinner
	const [isLoadingButtonCreateEmailTemplate, setIsLoadingButtonCreateEmailTemplate] = useState(null); // State for loading spinner
	const [isLoadingButtonDeleteEmailTemplate, setIsLoadingButtonDeleteEmailTemplate] = useState(null); // State for loading spinner
	const [isLoadingButtonSMTPTest, setIsLoadingButtonSMTPTest] = useState(false); // State for loading spinner
	const [isButtonDisabledSMTPTest, setIsButtonDisabledSMTPTest] = useState(false); // State for button disabled

	const auth = useAuthUser(); // Assuming this gets the auth user data
	const [formData, setFormData] = useState(null);
	const [data, setData] = useState(null); // Add data state

	useEffect(() => {
		// Check if the current tab is active before making the API call
		if (activeKey === 'emailTemplates') {
			getData();
		}
		setIsLoading(false);
	}, [activeKey]); // Run effect when activeKey changes

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async () => {
		setIsLoading(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/email/email-templates`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				const emailTemplates = responseData.map(template => ({
					// Assuming template.email_template_key and template.email_template_value are the correct keys
					id: template.id,
					value: template.id,
					label: template.email_template_key,
					email_template_value: template.email_template_value
				}));
				setData(emailTemplates);
				setIsLoading(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoading(false);
			});
	};

	const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
	const handleChange = (e) => {
		const { name, value } = e.target;

		// Update the formData state with the new value
		setFormData(prevFormData => {
			// Ensure email_template_value is always an object
			const updatedEmailTemplateValue = { ...prevFormData.email_template_value, [name]: value };
			return {
				...prevFormData,
				email_template_value: updatedEmailTemplateValue
			};
			
		});
	};

	// Update Email Template
	const handleUpdateEmailTemplate = async (e) => {
		e.preventDefault();
		setIsLoadingButton(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/email/email-templates/${formData.id}`, { email_template_key: formData.label, email_template_value: formData.email_template_value }, options)
			.then(response => {
				// Update the selected email template value in the data state
				setData(prevData => {
					// Find the selected template
					const selectedTemplate = prevData.find(template => template.value === selectedEmailTemplate);
					// Update the selected template's email_template_value
					if (selectedTemplate) {
						const updatedTemplate = {
							...selectedTemplate,
							email_template_value: {
								...selectedTemplate.email_template_value,
								...formData.email_template_value // Merge the updated values
							}
						};
						// Return the updated data
						return prevData.map(template => (template.value === selectedEmailTemplate ? updatedTemplate : template));
					}
					return prevData;
				});
				setIsLoadingButton(false);
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setIsLoadingButton(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Create Email Template
	const [showCreateEmailTemplateOffcanvas, setShowCreateEmailTemplateOffcanvas] = useState(false);
	const handleShowCreateEmailTemplate = () => setShowCreateEmailTemplateOffcanvas(true);
	const handleCloseCreateEmailTemplate = () => setShowCreateEmailTemplateOffcanvas(false);
	const [emailTemplateToDelete, setEmailTemplateToDelete] = useState(null); // State for user to delete
		

	const handleCreateEmailTemplate = async (e) => {
		e.preventDefault();

		//setIsButtonDisabled(true);
		setIsLoadingButtonCreateEmailTemplate(true); // Set loading to true

		const data = {
			email_template_key: e.target.key.value,
			email_template_value: {
				subject: e.target.subject.value,
				body: e.target.body.value
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		axios.post(`${baseURL}/email/email-templates`, data, options)
			.then(response => {
				getData(); // Refresh the data after creating a new email template
				// Reset the form fields
				e.target.reset();
				handleShowNotification(setNotification, 'success', 'Erfolgreich', response.data.message);
				setIsLoadingButtonCreateEmailTemplate(false); // Stop loading
				handleCloseCreateEmailTemplate();

			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte überprüfe deine eingegebenen Daten oder kontaktiere den HelpDesk.');
				setIsLoadingButtonCreateEmailTemplate(false); // Stop loading
			});
	};

	// Delete Email Template
	const [showDeleteModal, setShowDeleteModal] = useState(false); // State for showing modal
	const handleDeleteClick = (emailTemplate) => {
		setEmailTemplateToDelete(emailTemplate);
		setShowDeleteModal(true);
	};
	// Handle form submission
	const handleConfirmDelete = async () => {
		if (emailTemplateToDelete) {
			setIsLoadingButtonDeleteEmailTemplate(true);

			// Dynamische URL basierend auf der Umgebung
			const baseURL = process.env.REACT_APP_ENV === 'development'
				? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
				: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

			axios.delete(`${baseURL}/email/email-templates/${emailTemplateToDelete}`, options)
				.then(response => {
					setIsLoadingButtonDeleteEmailTemplate(false);
					setShowDeleteModal(false);
					// Reset the selected email template and form data
					setSelectedEmailTemplate('');
					setFormData(null);
					// Update the data state to remove the deleted template
					setData(prevData => prevData.filter(template => template.value !== emailTemplateToDelete));
					handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Email Template wurde erfolgreich gelöscht');
				})
				.catch((error) => {
					setIsLoadingButtonDeleteEmailTemplate(false);
					const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
					handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				})
		}
	};

	const handleSMTPTest = async () => {
		setIsLoadingButtonSMTPTest(true);
		setIsButtonDisabledSMTPTest(true);

		const body = {
			email_template_id: selectedEmailTemplate,
			message: {
				from: "haeppy.app <noreply@haeppy.app>",
				to: auth.username,
				variables: {
				}
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/email/send-email`, body, options)
			.then(response => {
				setIsLoadingButtonSMTPTest(false);
				setIsButtonDisabledSMTPTest(false);
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Die E-Mail wurde erfolgreich versendet.');
			})
			.catch((error) => {
				setIsLoadingButtonSMTPTest(false);
				setIsButtonDisabledSMTPTest(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage);
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				{/* Create new email template */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<div className="border-bottom pb-4 mb-4 d-flex align-items-center justify-content-end">
						<Button variant="secondary" className="me-2" onClick={handleShowCreateEmailTemplate}>
							{t('Create new email template')}
						</Button>
					</div>
				</Col>
			</Row>
			<Row>
				{/* Edit Email Template */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Edit email templates')}</h4>
						</Card.Header>
						<Card.Body>
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<Form onSubmit={handleUpdateEmailTemplate}>
									<Row className="">
										<Col lg={12} md={12} className="mb-3">
											<Form.Group className="mb-3" controlId="email">
												<Form.Label>
													{t('Email template')} <span className="text-danger">*</span>
												</Form.Label>
												<Form.Control
													as={FormSelect}
													defaultselected=""
													placeholder={selectedEmailTemplate ? '' : t('Choose email template')}
													options={data ? data : []}
													value={selectedEmailTemplate}
													onChange={(e) => {
														const selectedTemplate = data.find(template => template.value === e.target.value);
														setSelectedEmailTemplate(e.target.value);
														setFormData(selectedTemplate || {});
													}}
													required
												/>
											</Form.Group>
										</Col>
										{selectedEmailTemplate && (
											<>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group className="mb-3" controlId="emailSubject">
														<Form.Label>
															{t('Email template subject')} <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															as="textarea"
															name="subject"
															value={formData?.email_template_value.subject || ''}
															rows={2}
															onChange={(e) => {
																handleChange(e);
															}}
															required
														/>
													</Form.Group>
												</Col>
												<Col lg={12} md={12} className="mb-3">
													<Form.Group className="mb-3" controlId="emailBody">
														<Form.Label>
															{t('Email template body')} <span className="text-danger">*</span>
														</Form.Label>
														<Form.Control
															as="textarea"
															name="body"
															value={formData?.email_template_value.body || ''}
															rows={10}
															onChange={(e) => {
																handleChange(e);
															}}
															required
														/>
													</Form.Group>
												</Col>
											</>
										)}
									</Row>
									<Row>
										{selectedEmailTemplate && (
											<Col lg={12} md={12}>
												<div className="d-flex justify-content-end mt-4">
													<Button className="d-end me-2" variant="outline-primary" onClick={handleSMTPTest}>
														{isLoadingButtonSMTPTest ? (
															<>
																	<Spinner animation="border" size="sm" className="me-2" disabled={isButtonDisabledSMTPTest} />
																{t('Send test email')}
															</>
														) : (
																t('Send test email')
														)}
													</Button>
														<Button className="d-end me-2" variant="danger" onClick={() => handleDeleteClick(selectedEmailTemplate)}>
														{/* Delete Email Template */}
														{isLoadingButtonDeleteEmailTemplate ? (
															<>
																<Spinner animation="border" size="sm" className="me-2" />
																{t('Delete email template')}
															</>
														) : (
															t('Delete email template')
														)}
													</Button>
													<Button variant="primary" type="submit" disabled={isLoadingButton}>
														{isLoadingButton ? (
															<>
																<Spinner animation="border" size="sm" className="me-2" />
																{t('Save')}
															</>
														) : (
															t('Save')
														)}
													</Button>
												</div>
											</Col>
										)}
									</Row>
								</Form>
							)}
						</Card.Body>
					</Card>
				</Col>
			</Row>

			{/* Create Email Template Offcanvas */}
			<OffcanvasCreateEmailTemplate
				show={showCreateEmailTemplateOffcanvas}
				onHide={handleCloseCreateEmailTemplate}
				onSubmit={handleCreateEmailTemplate}
				isLoadingButton={isLoadingButtonCreateEmailTemplate}
			/>

			{/* Delete Email Temlate Modal */}
			<ModalDeleteEmailTemplate
				show={showDeleteModal}
				onHide={() => setShowDeleteModal(false)}
				handleConfirmDelete={handleConfirmDelete}
				isLoadingButton={isLoadingButtonDeleteEmailTemplate}
			/>
		</Fragment>
	);

};

export default EmailTemplateSettings;